import { getBooleanFromString } from '../../utils/string-utils';
import { isMobile, isSafari, isWidthDeviceMobileTablet } from '../../utils/device-utils';
import logger from '../../services/logging/logging-service';
import { initTvCardRail } from '../templating/tv-card-rail';
import { removeElementDom } from '../../utils/dom-utils';
import { skyFoundationSettings } from '../../services/configs/configs-service';
import {
  isEntertainmentDomain,
  isHomePage,
  isSectionHome,
  isStickyCarouselTemplateEnable,
  isSubSectionHome,
} from '../../utils/aem-utils';
import { getLocalStorage, removeLocalStorage } from '../../utils/local-storage';
import { commonActions } from '../../global-store/subjects/common';

import { createSkyPlayersManager } from './helpers';
import { VIDEO_PLAYER_STATUS } from './constants';

const PLAYER = 'j-player';
const PLAYER_WITH_CAROUSEL_ID_JS_CLASS = 'j-player-with-carousel';
const PLAYER_TV_CARD_RAIL_JS_CLASS = 'j-tv-card-rail';
const PLAYER_TYPE_CARD_RAIL = 'rail';
const PLAYER_TV_CARD_HEADER_CLASS = 'c-tv-card__header';
const HERO_PLAYER_CONTAINER = '.c-hero__media-wrapper .j-player';
const STICKY = 'sticky';
const HERO_PLAYER_TYPE = 'hero';
const HERO_HP_PLAYER_TYPE = 'hero - hp';
const BODY = 'body';
const STICKY_TRACK = '-sticky';
const HOOK_CAROUSEL_ELEMENT_STICK = '.j-player-carousel-element';
const SAFARI_PLAYER_CARD_CLASS = 'c-player-card-safari';
const isHomepageInArticle = skyFoundationSettings.getLazyLoadedHomepage();
const DEFAULT_DAI_OFF_VALUE = undefined;
const DEFAULT_SOCIAL_VALUE = undefined;
const isAudioOnRecirculationArticleEnabled = getLocalStorage('audio-on-recirculation-article-enabled') ? getLocalStorage('audio-on-recirculation-article-enabled') : false;

let heroLivePlayerInitialized = false;
let playerInBodyNumber = 0;
const skyPlayersManagerOnPage = createSkyPlayersManager();

const addSafariPlayerCardClass = () => {
  if (isSafari()) {
    const players = document.querySelectorAll(`.${PLAYER}`);
    players.forEach((player) => {
      player.classList.add(SAFARI_PLAYER_CARD_CLASS);
    });
  }
};

const mapPlayerCarousel = (player) => {
  player.wasInitialised = player.id !== PLAYER_WITH_CAROUSEL_ID_JS_CLASS ? true : null;
};

const getPlayerInPageList = ($players) => {
  const playerList = ($players ? [...$players] : [...document.querySelectorAll(`.${PLAYER}`)])
    .filter((item) => item.wasInitialised !== true)
    .filter((item) => item.dataset.videoid >= 1);
  logger.info(` [ INIT - Player ] LISTA PLAYER: ${playerList}`);
  playerList.forEach(mapPlayerCarousel);
  return playerList;
};

const getPlayerType = (playerType) => {
  if (playerType === BODY) {
    playerInBodyNumber++;
    return `${playerType}-${playerInBodyNumber}`;
  }
  return playerType;
};

const computedLive = (playerData) => {
  if (getBooleanFromString(playerData.dataset.islive) && !heroLivePlayerInitialized) {
    heroLivePlayerInitialized = true;
    return true;
  }
  return false;
};

const clearPlayersLive = () => {
  if (!isMobile() || isHomepageInArticle) {
    const livePlayersNodeList = Array.from(document.querySelectorAll('div[data-islive="true"]'));
    const isLivePlayerInHero = livePlayersNodeList.filter((element) => element.dataset.playertype === HERO_PLAYER_TYPE);
    if (isLivePlayerInHero.length > 0) {
      // is live in hero, delete tv card box not in Entertainment.
      if (!isEntertainmentDomain()) {
        removeElementDom(document.querySelector(`.${PLAYER_TV_CARD_RAIL_JS_CLASS}`));
        livePlayersNodeList.forEach((livePlayer) => {
          if (livePlayer.dataset.playertype !== HERO_PLAYER_TYPE) {
            removeElementDom(livePlayer);
          }
        });
      }
    }
  }
};

const getPreviewImg = (previewimg) => {
  if (!previewimg) {
    return true;
  }
  if (previewimg === 'false') {
    return false;
  }
  return previewimg;
};

/**
 * Build player setup object
 * @param {*} playerData dataset of player
 * @param {boolean} isLive for live streaming
 */
const buildPlayerSetup = ({ playerData, isLive, iasTracker, playerType }) => {
  const isPlayerMuted = () => {
    if (isAudioOnRecirculationArticleEnabled && getBooleanFromString(playerData.dataset.audioOnRecirculationPlayer)) {
      return false;
    }
    return playerData.dataset.mute ? getBooleanFromString(playerData.dataset.mute) : true;
  };

  return {
    id: playerData.dataset.videoid || '',
    live: playerData.dataset.islive ? getBooleanFromString(playerData.dataset.islive) : false,
    advOff: playerData.dataset.advoff ? getBooleanFromString(playerData.dataset.advoff) : false,
    domain: 'sky',
    autostart: playerData.dataset.autostart ? getBooleanFromString(playerData.dataset.autostart) : true,
    trackdomain: window.location.hostname || '',
    playertype: playerType,
    preview: getPreviewImg(playerData.dataset.previewimg),
    ias: iasTracker,
    nextVideo: playerData.dataset.nextVideo ? getBooleanFromString(playerData.dataset.nextVideo) : false,
    countdown: playerData.dataset.countdown ? getBooleanFromString(playerData.dataset.countdown) : 5000,
    daiOff: isLive ? getBooleanFromString(skyFoundationSettings.getDaiOff()) : DEFAULT_DAI_OFF_VALUE,
    social: playerData.dataset.social ? getBooleanFromString(playerData.dataset.social) : DEFAULT_SOCIAL_VALUE,
    mute: isPlayerMuted(),
  };
};

/**
 * Build player Configuration depending on playerData
 * @param {*} playerData dataset of player
 */
const playerConfBuilder = (playerData) => {
  const containerid = playerData.id;
  let playerType = playerData.dataset.playertype ? getPlayerType(playerData.dataset.playertype) : '';
  const $heroPlayerContainer = document.querySelector(HERO_PLAYER_CONTAINER);
  const valueDataset = $heroPlayerContainer ? $heroPlayerContainer.dataset.islive : 'false';
  const playerDataset = {
    isLive: computedLive(playerData),
    isRailType: playerType === PLAYER_TYPE_CARD_RAIL,
    isHeroType: playerType === 'hero' && !computedLive(playerData),
    isCarouselType: playerType.includes('carousel'),
  };
  const iasTracker = Boolean(
    playerDataset.isHeroType || (playerDataset.isCarouselType && (valueDataset === 'true' || !$heroPlayerContainer))
  );
  const stickyCarouselElement =
    document.querySelector(HOOK_CAROUSEL_ELEMENT_STICK)?.dataset?.stickycarouselactive === 'true';
  const stickyCarouselTemplateTrack = isStickyCarouselTemplateEnable();
  if (stickyCarouselTemplateTrack && stickyCarouselElement && playerDataset.isCarouselType) {
    playerType = playerType.concat(STICKY_TRACK);
  }
  if (playerDataset.isRailType) {
    playerType = STICKY;
  }
  const playersetup = buildPlayerSetup({
    playerData,
    isLive: playerDataset.isLive,
    iasTracker,
    playerType,
  });

  if ((isMobile() || isWidthDeviceMobileTablet()) && playerDataset.isRailType) {
    logger.info(` [ INIT - Player ] is Mobile Player and playerType = ${PLAYER_TYPE_CARD_RAIL}`);
    const $tvCard = document.querySelector(`.${PLAYER_TV_CARD_RAIL_JS_CLASS}`);
    logger.info('[ INIT - Player ] before tv card');
    logger.info('[TV - CARD] before tv card');
    initTvCardRail({
      $tvCard,
      isLive: playerDataset.isLive,
      isLiveDesktop: false,
    });
    return null;
  }

  if (!isMobile() && playerDataset.isRailType) {
    logger.info(' [ INIT - Player ] is not mobile');

    if (playerDataset.isLive) {
      logger.info(' [ INIT - Player ] playerType is rail');
      playersetup.live = playerDataset.isLive;
      playersetup.playertype = STICKY;
    }

    if (!playerDataset.isLive) {
      logger.info(' [ INIT - Player ] remove video Box Rail');
      const $tvCard = document.querySelector(`.${PLAYER_TV_CARD_RAIL_JS_CLASS}`);
      const $tvCardHeader = $tvCard.querySelector(`.${PLAYER_TV_CARD_HEADER_CLASS}`);
      logger.info('[TV - CARD] players tv card');
      initTvCardRail({
        $tvCard,
        isLiveDesktop: true,
      });
    }
  }

  if (!isMobile() && !playerDataset.isRailType) {
    logger.info(' [ INIT - Player ] playerType is not rail');
    playersetup.live = playerDataset.isLive;
  }

  if (
    (isHomePage() || isSectionHome() || isSubSectionHome()) &&
    !playersetup.autostart &&
    playersetup.playertype === HERO_PLAYER_TYPE
  ) {
    playersetup.playertype = HERO_HP_PLAYER_TYPE;
  }

  logger.info(
    ` [ INIT - Player ] return player config containerId:${containerid} playerSetup:${JSON.stringify(playersetup)}`
  );
  return {
    containerid,
    playersetup,
  };
};

/**
 * Get Player Config List
 */
const getPlayerConfigList = ($players) => {
  return [...getPlayerInPageList($players)].map(playerConfBuilder).filter((playerConf) => Boolean(playerConf));
};

/**
 *
 * @param {*} $players is an OPTIONAL NodeList
 * @returns { playerList, playerConfigList }
 */
export const initPlayers = ($players) => {
  addSafariPlayerCardClass();
  clearPlayersLive();
  const playerConfigList = getPlayerConfigList($players);
  const playerList = skyPlayersManagerOnPage.init().initPlayerList(playerConfigList);
  if (isAudioOnRecirculationArticleEnabled) {
    // ITE-4474 when can start in audio on in autoplay the volume is set to 10%
    playerConfigList.forEach((playerConfig, index) => {
      const $currentPlayer = document.getElementById(playerConfig.containerid);
      const isAudioOnRecirculationPlayer = getBooleanFromString($currentPlayer.dataset.audioOnRecirculationPlayer);
      if (isAudioOnRecirculationPlayer) {
        playerList[index].on(VIDEO_PLAYER_STATUS.READY, async () => {
          playerList[index].setVolume(10);
        });
      }
    });

    removeLocalStorage('audio-on-recirculation-article-enabled');
  }
  commonActions.setPlayersOnPage(playerList);
  return { playerList, playerConfigList };
};
